import type { ProgramMember } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete2LineIcon, Modal, message } from 'ui';
import { useDeleteProgramMemberMutation } from '../../queries';

export type ProgramMemberForDeleteConfirm = Pick<ProgramMember, 'fullName' | 'id' | 'joined'>;

export const useConfirmDeleteProgramMember = () => {
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const { isPending, mutate: deleteProgramMember } = useDeleteProgramMemberMutation();

  const confirmDeleteMember = useCallback(
    ({
      member,
      programId,
      programName,
    }: {
      member: ProgramMemberForDeleteConfirm;
      programId: string;
      programName: string;
    }) => {
      modal.confirm({
        content: 'This action is irreversible.',
        icon: <Delete2LineIcon type="error" />,
        okText: 'Yes, remove',
        onOk() {
          if (!member.joined) {
            message.warn('User with pending invitation cannot be removed');
            return;
          }
          deleteProgramMember(
            { programId, userId: member.id },
            {
              onSuccess() {
                message.success('User removed successfully');
              },
            },
          );
        },
        title: t('Remove user from?', {
          fullName: member.fullName,
          groupName: programName,
        }),
        type: 'error',
      });
    },
    [deleteProgramMember, modal, t],
  );

  return useMemo(
    () => ({ confirmDeleteMember, contextHolder, isLoading: isPending }),
    [confirmDeleteMember, contextHolder, isPending],
  );
};
